import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Stack,
  Autocomplete,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Form, FormikProvider, useFormik } from 'formik';
import { useQuery } from '@tanstack/react-query';
import Iconify from '../../../components/iconify';
import { INITIAL_ESCALATION_FORM_STATE, ESCALATION_SEVERITY, ESCALATION_SEVERITY_LABELS } from '../constants';
import EscalationSchema from '../schema/escalationSchema';
import { getAllProjects } from '../../../services/projectService';

const EscalationForm = ({ open, onClose, onSubmit, escalationData = null, isEdit = false }) => {
  // Fetch user's projects
  const { data: userProjectsResponse } = useQuery({
    queryKey: ['userProjects'],
    queryFn: async () => {
      try {
        const response = await getAllProjects({ isMemberOnly: true });
        return response?.data?.data?.rows || [];
      } catch (error) {
        console.error('Error fetching user projects:', error);
        return [];
      }
    },
    enabled: open,
    staleTime: 60000, // Cache for 1 minute
  });

  // Initialize formik
  const getInitialValues = () => {
    if (isEdit && escalationData) {
      return {
        projectId: escalationData.projectId || '',
        description: escalationData.description || '',
        severity: escalationData.severity || ESCALATION_SEVERITY.MEDIUM,
      };
    }
    return INITIAL_ESCALATION_FORM_STATE;
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema: EscalationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setSubmitting(true);
      try {
        if (isEdit) {
          delete values.projectId; // remove projectId from values if it is edit
        }
        await onSubmit(values);
        if (!isEdit) {
          resetForm({ values: INITIAL_ESCALATION_FORM_STATE });
        }
      } catch (error) {
        // Error is handled by mutation's onError
      } finally {
        setSubmitting(false);
      }
    },
    enableReinitialize: true,
  });

  const { values, errors, touched, isSubmitting, setFieldValue } = formik;

  // Find selected project
  const selectedProject = useMemo(() => {
    if (!values.projectId || !userProjectsResponse) return null;
    return userProjectsResponse.find((p) => p.id === values.projectId) || null;
  }, [userProjectsResponse, values.projectId]);

  // Stable option equality check
  const isOptionEqualToValue = (option, value) => {
    if (!value || !option) return false;
    return option.id === value.id;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Escalation' : 'Create Escalation'}</DialogTitle>
      <FormikProvider value={formik}>
        <Form>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              {/* Project Selection */}
              <Autocomplete
                options={userProjectsResponse || []}
                getOptionLabel={(option) => option?.projectName || ''}
                value={selectedProject}
                onChange={(event, newValue) => {
                  setFieldValue('projectId', newValue?.id || '');
                }}
                isOptionEqualToValue={isOptionEqualToValue}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Project *"
                    error={touched.projectId && Boolean(errors.projectId)}
                    helperText={touched.projectId && errors.projectId}
                    placeholder="Select a project"
                  />
                )}
                disabled={isEdit}
              />

              {/* Description */}
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description *"
                name="description"
                value={values.description}
                onChange={(e) => setFieldValue('description', e.target.value)}
                error={touched.description && Boolean(errors.description)}
                helperText={touched.description && errors.description}
                placeholder="Enter escalation description..."
                required
              />

              {/* Severity Selection */}
              <FormControl fullWidth error={touched.severity && Boolean(errors.severity)}>
                <InputLabel>Severity *</InputLabel>
                <Select
                  name="severity"
                  value={values.severity}
                  label="Severity *"
                  onChange={(e) => setFieldValue('severity', e.target.value)}
                >
                  {Object.entries(ESCALATION_SEVERITY_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
                {touched.severity && errors.severity && <FormHelperText>{errors.severity}</FormHelperText>}
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isSubmitting}
              startIcon={<Iconify icon="eva:checkmark-fill" />}
            >
              {isEdit ? 'Update' : 'Create'}
            </LoadingButton>
          </DialogActions>
        </Form>
      </FormikProvider>
    </Dialog>
  );
};

EscalationForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  escalationData: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default EscalationForm;
